import { useEffect, useState, type ReactNode } from 'react';
import { Plane } from 'lucide-react';

const STORAGE_KEY = 'rostfound-splash-shown';

/**
 * SplashScreen — shows an animated intro on first load per browser session.
 * Wraps its children; once the splash sequence finishes, children are revealed.
 */
export function SplashScreen({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<
    'idle' | 'animating' | 'fading-out' | 'done'
  >(() => {
    // Skip the splash entirely if already shown this session
    if (typeof window !== 'undefined') {
      try {
        if (sessionStorage.getItem(STORAGE_KEY)) return 'done';
      } catch {
        // sessionStorage blocked — skip splash gracefully
        return 'done';
      }
    }
    return 'idle';
  });

  useEffect(() => {
    if (phase === 'done') return;

    // Kick off the animation on the next frame so the initial render paints first
    const startTimer = requestAnimationFrame(() => setPhase('animating'));

    return () => cancelAnimationFrame(startTimer);
  }, []);

  useEffect(() => {
    if (phase !== 'animating') return;

    // After 1.5 s of animation, begin the fade-out
    const fadeTimer = setTimeout(() => setPhase('fading-out'), 1500);
    return () => clearTimeout(fadeTimer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'fading-out') return;

    // Wait for the 500 ms fade-out transition, then mark as done
    const doneTimer = setTimeout(() => {
      setPhase('done');
      try {
        sessionStorage.setItem(STORAGE_KEY, '1');
      } catch {
        // ignore
      }
    }, 500);
    return () => clearTimeout(doneTimer);
  }, [phase]);

  if (phase === 'done') return <>{children}</>;

  const isAnimating = phase === 'animating' || phase === 'fading-out';
  const isFadingOut = phase === 'fading-out';

  return (
    <>
      {/* Inject keyframes once — scoped names avoid collisions */}
      <style>{`
        @keyframes splashPlaneFly {
          0% {
            transform: translateX(-120px) translateY(20px) rotate(-10deg);
            opacity: 0;
          }
          40% {
            opacity: 1;
          }
          100% {
            transform: translateX(0) translateY(0) rotate(0deg);
            opacity: 1;
          }
        }
        @keyframes splashTitleFadeIn {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes splashTaglineFadeIn {
          0% {
            opacity: 0;
            transform: translateY(8px);
          }
          100% {
            opacity: 0.7;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Splash overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-slate-900)',
          transition: 'opacity 500ms ease-out',
          opacity: isFadingOut ? 0 : 1,
        }}
      >
        {/* Plane icon */}
        <div
          style={{
            opacity: 0,
            ...(isAnimating
              ? {
                  animation: 'splashPlaneFly 700ms ease-out forwards',
                }
              : {}),
          }}
        >
          <Plane
            size={56}
            strokeWidth={1.5}
            style={{ color: 'var(--color-accent-val)' }}
          />
        </div>

        {/* App name */}
        <h1
          style={{
            marginTop: 16,
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: '-0.025em',
            color: 'var(--color-slate-100)',
            opacity: 0,
            ...(isAnimating
              ? {
                  animation: 'splashTitleFadeIn 500ms ease-out 400ms forwards',
                }
              : {}),
          }}
        >
          Rost &amp; Found
        </h1>

        {/* Tagline */}
        <p
          style={{
            marginTop: 8,
            fontSize: 14,
            color: 'var(--color-slate-500)',
            opacity: 0,
            ...(isAnimating
              ? {
                  animation:
                    'splashTaglineFadeIn 500ms ease-out 700ms forwards',
                }
              : {}),
          }}
        >
          Your crew, everywhere
        </p>
      </div>
    </>
  );
}
